/* Admin Center — shared types + tab-group config (extracted from AdminPage.tsx) */

import type { AppModule } from '../../../shared/modules';

interface AdminPageProps {
  toast: (msg: string, isError?: boolean) => void;
  userPermissions?: string[];
  userEmail?: string;
  /** Modules the firm has licensed — hides role-builder categories the firm
   *  can't use (R3.4). Undefined/empty means "everything enabled" (an
   *  unseeded firm is never locked out), matching the server fallback. */
  enabledModules?: AppModule[];
}

type Tab =
  | 'roles'
  | 'billingRoles'
  | 'departments'
  | 'projectTypes'
  | 'billingTypes'
  | 'activityCodes'
  | 'phases'
  | 'overheadCategories'
  | 'expenseTypes'
  | 'scopes'
  | 'vacationCategories'
  | 'firmHolidays'
  | 'import'
  | 'harSync'
  | 'subscription'
  | 'activity';
// 'companyRoles' removed in v2.9.0 — merged into Titles (the renamed
// Billing Roles tab). The dead CompanyRolesTab component was deleted
// from this file during the spec-masters-repair pass.

// ─── Tab groups: each group has a permission gate and a set of tabs ───
interface TabGroup {
  label: string;
  /** Permission key required to see this group. null = always visible if user can access admin center. */
  permission: string | null;
  /** Per-tab override — falls back to the group's `permission` when unset. */
  tabs: { id: Tab; label: string; permission?: string }[];
}

const TAB_GROUPS: TabGroup[] = [
  {
    label: 'Roles & Access',
    permission: null,
    tabs: [
      { id: 'roles', label: 'Roles & Privileges', permission: 'admin_center.manage_roles' },
      // R1.7 / ledger bug 3: Departments has its own server-side check
      // (admin_center.manage_departments) — it must not ride manage_roles.
      { id: 'departments', label: 'Departments', permission: 'admin_center.manage_departments' },
    ],
  },
  {
    label: 'Billing & Project Setup',
    permission: 'admin_center.manage_billing',
    tabs: [
      { id: 'billingRoles', label: 'Titles' },
      { id: 'projectTypes', label: 'Project Types' },
      { id: 'billingTypes', label: 'Fee Structures' },
      { id: 'activityCodes', label: 'Billing Codes' },
      { id: 'phases', label: 'Phases' },
      { id: 'overheadCategories', label: 'Overhead Categories' },
      { id: 'expenseTypes', label: 'Expense Types' },
      { id: 'scopes', label: 'Project Scopes' },
    ],
  },
  {
    label: 'Time Off',
    permission: 'admin_center.manage_billing',
    tabs: [
      { id: 'vacationCategories', label: 'Vacation Categories' },
      { id: 'firmHolidays', label: 'Firm Holidays' },
    ],
  },
  {
    label: 'Data',
    permission: 'admin_center.manage_billing',
    tabs: [{ id: 'import', label: 'CSV Import' }],
  },
  {
    label: 'Subscription',
    permission: 'admin_center.manage_subscription',
    tabs: [{ id: 'subscription', label: 'Subscription' }],
  },
  {
    label: 'Audit',
    permission: 'audit_log.view_all',
    tabs: [{ id: 'activity', label: 'Activity Log' }],
  },
];


export type { AdminPageProps, Tab, TabGroup };
export { TAB_GROUPS };
